export function normalizeTreeResponse(payload) {
    return Array.isArray(payload) ? payload : [];
}

export function countTreeNodes(nodes) {
    if (!Array.isArray(nodes)) return 0;
    return nodes.reduce(
        (sum, node) => sum + 1 + countTreeNodes(node?.children),
        0
    );
}

export function findTreeNodeById(nodes, id) {
    if (!Array.isArray(nodes) || id == null) return undefined;
    for (const node of nodes) {
        if (node?.id === id) return node;
        const found = findTreeNodeById(node?.children, id);
        if (found) return found;
    }
    return undefined;
}

export function levelTreeNodes(nodes, level = 1) {
    return normalizeTreeResponse(nodes).map((item) => {
        const children = Array.isArray(item?.children) && item.children.length > 0
            ? levelTreeNodes(item.children, level + 1)
            : [];
        return {
            ...item,
            level,
            children,
            toggled: children.length > 0,
        };
    });
}

export function collectTreeIds(nodes) {
    const ids = [];
    normalizeTreeResponse(nodes).forEach((node) => {
        if (node?.id != null) ids.push(node.id);
        ids.push(...collectTreeIds(node?.children));
    });
    return ids;
}

export function nodeNameMatches(node, filterText) {
    const name = String(node?.name ?? "");
    const filter = String(filterText ?? "").trim().toLowerCase();
    if (!filter) return true;
    return name.toLowerCase().includes(filter);
}

const nodeOrDescendantMatches = (node, filter, matcher) => {
    if (matcher(node, filter)) return true;
    return normalizeTreeResponse(node?.children).some((child) =>
        nodeOrDescendantMatches(child, filter, matcher)
    );
};

export function filterTree(node, filter, matcher = nodeNameMatches) {
    if (!node) return { name: "Root", children: [] };
    if (matcher(node, filter) || !Array.isArray(node.children)) {
        return node;
    }
    const filtered = node.children
        .filter((child) => nodeOrDescendantMatches(child, filter, matcher))
        .map((child) => filterTree(child, filter, matcher));
    return { ...node, children: filtered };
}

export function expandFilteredNodes(node, filter, matcher = nodeNameMatches) {
    const children = normalizeTreeResponse(node?.children);
    if (children.length === 0) {
        return { ...node, children: [], toggled: false };
    }
    const childrenWithMatches = children.filter((child) =>
        nodeOrDescendantMatches(child, filter, matcher)
    );
    const shouldExpand = childrenWithMatches.length > 0;
    return {
        ...node,
        children: shouldExpand
            ? childrenWithMatches.map((child) =>
                  expandFilteredNodes(child, filter, matcher)
              )
            : children,
        toggled: shouldExpand,
    };
}

export function preserveToggleState(nextNodes, previousNodes) {
    const previous = normalizeTreeResponse(previousNodes);
    return normalizeTreeResponse(nextNodes).map((item) => {
        const prior = findTreeNodeById(previous, item?.id);
        const children = normalizeTreeResponse(item?.children);
        const hadToggleState =
            prior != null && Object.prototype.hasOwnProperty.call(prior, "toggled");
        return {
            ...item,
            toggled: hadToggleState ? prior.toggled : children.length > 0,
            active: prior?.active,
            children:
                children.length > 0
                    ? preserveToggleState(children, prior?.children)
                    : [],
        };
    });
}
